import { PlusIcon } from '@shopify/polaris-icons';
import { useMoney } from '../hooks/useMoney';
import { useEffect, useState, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Select,
  DataTable,
  EmptyState,
  Spinner,
  Banner,
  Modal,
  TextField,
  Divider,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';

interface ValuationItem {
  product_id: number;
  sku: string;
  title: string;
  vendor: string;
  batch_id: number;
  batch_number: string;
  location_id: number;
  location_name: string;
  warehouse_id: number;
  warehouse_name: string;
  quantity: number;
  unit_cost: number;
  total_cost: number;
  received_date: string;
  expiry_date: string | null;
  age_days: number;
  days_to_expiry: number | null;
}

interface Totals {
  total_units: number;
  total_cost: number;
  total_retail_value: number;
  product_count: number;
  batch_count: number;
}

interface Snapshot {
  id: number;
  snapshot_date: string;
  snapshot_type: string;
  total_units: number;
  total_cost: number;
  product_count: number;
  batch_count: number;
  costing_method: string;
  generated_at: string;
  generated_by: string;
  notes: string;
}

interface Warehouse {
  id: number;
  name: string;
}

export default function StockValuation() {
  const navigate = useNavigate();
  const { fetchApi, error, loading } = useApi();

  const [items, setItems] = useState<ValuationItem[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [source, setSource] = useState<'calculated' | 'snapshot'>('calculated');
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);

  // Filters
  const [valuationDate, setValuationDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [warehouseId, setWarehouseId] = useState('');
  const [includeZero, setIncludeZero] = useState(false);

  // Snapshot modal
  const [showSnapshotModal, setShowSnapshotModal] = useState(false);
  const [snapshotType, setSnapshotType] = useState('manual');
  const [snapshotNotes, setSnapshotNotes] = useState('');
  const [creatingSnapshot, setCreatingSnapshot] = useState(false);

  const loadWarehouses = useCallback(async () => {
    const result = await fetchApi<{ warehouses: Warehouse[] }>('/inventory/warehouses');
    if (result) {
      setWarehouses(result.warehouses || []);
    }
  }, [fetchApi]);

  const loadSnapshots = useCallback(async () => {
    const result = await fetchApi<{ snapshots: Snapshot[] }>('/costing/valuation/snapshots');
    if (result) {
      setSnapshots(result.snapshots || []);
    }
  }, [fetchApi]);

  const loadValuation = useCallback(async () => {
    let url = `/costing/valuation?date=${valuationDate}`;
    if (warehouseId) url += `&warehouse_id=${warehouseId}`;
    if (includeZero) url += `&include_zero_stock=true`;

    const result = await fetchApi<{
      source: 'calculated' | 'snapshot';
      date: string;
      totals: Totals;
      items: ValuationItem[];
    }>(url);

    if (result) {
      setItems(result.items || []);
      setTotals(result.totals);
      setSource(result.source);
    }
  }, [fetchApi, valuationDate, warehouseId, includeZero]);

  useEffect(() => {
    loadWarehouses();
    loadSnapshots();
  }, [loadWarehouses, loadSnapshots]);

  useEffect(() => {
    loadValuation();
  }, [loadValuation]);

  const handleCreateSnapshot = async () => {
    setCreatingSnapshot(true);
    try {
      const result = await fetchApi<{ success: boolean; snapshot_id: number }>('/costing/valuation/snapshot', {
        method: 'POST',
        body: JSON.stringify({
          date: valuationDate,
          snapshot_type: snapshotType,
          notes: snapshotNotes,
        }),
      });

      if (result?.success) {
        setShowSnapshotModal(false);
        setSnapshotNotes('');
        loadSnapshots();
        loadValuation();
      }
    } finally {
      setCreatingSnapshot(false);
    }
  };

  const formatCurrency = useMoney();

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-GB').format(value);
  };

  const warehouseOptions = [
    { label: 'All Warehouses', value: '' },
    ...warehouses.map((w) => ({ label: w.name, value: w.id.toString() })),
  ];

  const snapshotTypeOptions = [
    { label: 'Manual', value: 'manual' },
    { label: 'Month End', value: 'monthly' },
    { label: 'Year End', value: 'year_end' },
    { label: 'Audit', value: 'audit' },
  ];

  // Group by product for summary view
  const productSummary = items.reduce((acc: Record<number, {
    product_id: number;
    sku: string;
    title: string;
    total_quantity: number;
    total_cost: number;
    batch_count: number;
  }>, item) => {
    if (!acc[item.product_id]) {
      acc[item.product_id] = {
        product_id: item.product_id,
        sku: item.sku,
        title: item.title,
        total_quantity: 0,
        total_cost: 0,
        batch_count: 0,
      };
    }
    acc[item.product_id].total_quantity += item.quantity;
    acc[item.product_id].total_cost += item.total_cost;
    acc[item.product_id].batch_count++;
    return acc;
  }, {});

  const summaryRows = Object.values(productSummary)
    .sort((a, b) => b.total_cost - a.total_cost)
    .map((p) => [
      <Button key={p.product_id} variant="plain" onClick={() => navigate(`/products/${p.product_id}`)}>
        {p.sku}
      </Button>,
      <div key={`title-${p.product_id}`} style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {p.title}
      </div>,
      formatNumber(p.total_quantity),
      `${p.batch_count}`,
      formatCurrency(p.total_cost / p.total_quantity),
      formatCurrency(p.total_cost),
    ]);

  const detailRows = items.map((item) => [
    item.sku,
    item.batch_number,
    item.warehouse_name || '-',
    item.location_name || '-',
    formatNumber(item.quantity),
    formatCurrency(item.unit_cost || 0),
    formatCurrency(item.total_cost),
    item.received_date,
    item.expiry_date || '-',
    item.age_days !== null ? `${item.age_days}d` : '-',
    item.days_to_expiry !== null ? (
      item.days_to_expiry < 0 ? (
        <Badge tone="critical">Expired</Badge>
      ) : item.days_to_expiry < 30 ? (
        <Badge tone="warning">{`${item.days_to_expiry}d`}</Badge>
      ) : (
        `${item.days_to_expiry}d`
      )
    ) : '-',
  ]);

  return (
    <Page
      title="Stock Valuation"
      subtitle="View stock value at any date for auditor reports"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
      primaryAction={{
        icon: PlusIcon, content: 'Create Snapshot',
        onAction: () => setShowSnapshotModal(true),
      }}
      secondaryActions={[
        {
          content: 'View Snapshots',
          onAction: () => navigate('/reports/valuation-snapshots'),
        },
      ]}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {source === 'snapshot' && (
          <Layout.Section>
            <Banner tone="info" title="Using Snapshot Data">
              <Text as="p" variant="bodyMd">
                This valuation is from a saved snapshot. For live data, select a date without a snapshot.
              </Text>
            </Banner>
          </Layout.Section>
        )}

        {/* Summary Cards */}
        {totals && (
          <Layout.Section>
            <InlineStack gap="400" wrap={false}>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Total Value</Text>
                  <Text variant="heading2xl" as="p">{formatCurrency(totals.total_cost)}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">At cost price</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Total Units</Text>
                  <Text variant="heading2xl" as="p">{formatNumber(totals.total_units)}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">In stock</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Products</Text>
                  <Text variant="heading2xl" as="p">{totals.product_count}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">Unique SKUs</Text>
                </BlockStack>
              </Card>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h3">Batches</Text>
                  <Text variant="heading2xl" as="p">{totals.batch_count}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">With stock</Text>
                </BlockStack>
              </Card>
            </InlineStack>
          </Layout.Section>
        )}

        {/* Filters */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Valuation Date</Text>
              <InlineStack gap="400" align="start">
                <div style={{ width: 180 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Date</label>
                  <input
                    type="date"
                    value={valuationDate}
                    onChange={(e) => setValuationDate(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ccc', width: '100%' }}
                  />
                </div>
                <div style={{ width: 180 }}>
                  <Select
                    label="Warehouse"
                    options={warehouseOptions}
                    value={warehouseId}
                    onChange={setWarehouseId}
                  />
                </div>
                <div style={{ paddingTop: 24 }}>
                  <Button onClick={() => setIncludeZero(!includeZero)}>
                    {includeZero ? 'Hide Zero Stock' : 'Show Zero Stock'}
                  </Button>
                </div>
              </InlineStack>
              <Text as="p" variant="bodySm" tone="subdued">
                Select a date to see the stock valuation as of that date. Create snapshots for official audit records.
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Recent Snapshots */}
        {snapshots.length > 0 && (
          <Layout.Section>
            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd" as="h2">Recent Snapshots</Text>
                <InlineStack gap="300" wrap>
                  {snapshots.slice(0, 5).map((s) => (
                    <div key={s.id} onClick={() => setValuationDate(s.snapshot_date)} style={{ cursor: 'pointer' }}>
                      <Badge tone={s.snapshot_type === 'year_end' ? 'success' : 'info'}>
                        {`${s.snapshot_date} (${s.snapshot_type})`}
                      </Badge>
                    </div>
                  ))}
                </InlineStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* Summary Table */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Valuation by Product</Text>
              {loading ? (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <Spinner size="large" />
                </div>
              ) : summaryRows.length === 0 ? (
                <EmptyState
                  heading="No stock data"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>No batches found for the selected date and filters.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'numeric', 'numeric', 'numeric', 'numeric']}
                  headings={['SKU', 'Product', 'Quantity', 'Batches', 'Avg Cost', 'Total Value']}
                  rows={summaryRows}
                  footerContent={`${Object.keys(productSummary).length} products`}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Batch Detail Table */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">Batch Details</Text>
              {!loading && detailRows.length > 0 && (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'text', 'text', 'text', 'text']}
                  headings={['SKU', 'Batch', 'Warehouse', 'Location', 'Qty', 'Unit Cost', 'Total', 'Received', 'Expires', 'Age', 'Expiry']}
                  rows={detailRows}
                  footerContent={`${items.length} batches`}
                />
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Info Section */}
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Text variant="headingMd" as="h2">About Stock Valuation</Text>
              <Text as="p" variant="bodyMd">
                Stock valuation shows the total value of inventory at cost price. Use snapshots to create
                official records for auditor reports, month-end, or year-end closing.
              </Text>
              <Divider />
              <BlockStack gap="200">
                <Text variant="headingSm" as="h3">Costing Methods</Text>
                <InlineStack gap="400" wrap>
                  <Badge>FIFO - First In, First Out</Badge>
                  <Badge>FEFO - First Expired, First Out</Badge>
                  <Badge>LIFO - Last In, First Out</Badge>
                  <Badge>Average Cost</Badge>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Create Snapshot Modal */}
      <Modal
        open={showSnapshotModal}
        onClose={() => setShowSnapshotModal(false)}
        title="Create Valuation Snapshot"
        primaryAction={{
          icon: PlusIcon, content: 'Create Snapshot',
          onAction: handleCreateSnapshot,
          loading: creatingSnapshot,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setShowSnapshotModal(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text as="p" variant="bodyMd">
              Create a snapshot of the current stock valuation for {valuationDate}. This will be saved
              as an official record for auditor reports.
            </Text>
            <Select
              label="Snapshot Type"
              options={snapshotTypeOptions}
              value={snapshotType}
              onChange={setSnapshotType}
            />
            <TextField
              label="Notes"
              value={snapshotNotes}
              onChange={setSnapshotNotes}
              multiline={3}
              autoComplete="off"
              placeholder="Optional notes for this snapshot..."
            />
            {totals && (
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingSm" as="h3">Snapshot Summary</Text>
                  <InlineStack gap="400">
                    <Text as="span" variant="bodyMd">Value: {formatCurrency(totals.total_cost)}</Text>
                    <Text as="span" variant="bodyMd">Units: {formatNumber(totals.total_units)}</Text>
                    <Text as="span" variant="bodyMd">Products: {totals.product_count}</Text>
                  </InlineStack>
                </BlockStack>
              </Card>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
